import React, { useState } from 'react';
import { splitCrmLead } from '../config/api';

const EXTERNAL_ID_LABELS = {
  pmsg_vendor_id: 'PMSG Vendor ID',
  oms_user_id: 'OMS Account',
  gst: 'GST',
};

const externalIdValue = (externalId) => {
  if (externalId.type !== 'oms_user_id') return externalId.value;
  const account = externalId.omsAccount;
  if (!account) return `Account #${externalId.value}`;
  return [account.name, account.phone].filter(Boolean).join(' · ');
};

const CrmSplitLeadDialog = ({ currentLead, onClose, onSplit }) => {
  const contacts = currentLead.contacts || [];
  const externalIds = currentLead.externalIds || [];
  const [newLeadName, setNewLeadName] = useState(
    `${currentLead.displayName || `Lead #${currentLead.id}`} (split)`
  );
  const [contactIds, setContactIds] = useState([]);
  const [externalIdIds, setExternalIdIds] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleId = (id, setter) => {
    setter((selected) => (
      selected.includes(id)
        ? selected.filter((selectedId) => selectedId !== id)
        : [...selected, id]
    ));
    setConfirmed(false);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!newLeadName.trim()) {
      setError('Enter a name for the new Lead.');
      return;
    }
    if (!contactIds.length && !externalIdIds.length) {
      setError('Select at least one contact or linked identifier.');
      return;
    }
    if (!confirmed) {
      setError('Confirm the selected records before splitting.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await splitCrmLead(currentLead.id, {
        newLeadName: newLeadName.trim(),
        contactIds,
        externalIdIds,
      });
      if (onSplit) onSplit(response?.data || response);
    } catch (err) {
      setError(err.message || 'Could not split the Lead.');
    } finally {
      setSaving(false);
    }
  };

  const hasSelection = contactIds.length > 0 || externalIdIds.length > 0;

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content crm-split-modal" onClick={(event) => event.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <div>
              <h3>Split Lead</h3>
              <div className="crm-muted">Move selected records into a new Lead.</div>
            </div>
            <button type="button" className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>

          <div className="modal-body">
            <div className="form-group">
              <label className="form-label" htmlFor="crm-split-lead-name">New Lead name</label>
              <input
                id="crm-split-lead-name"
                className="form-control"
                value={newLeadName}
                maxLength={255}
                onChange={(event) => {
                  setNewLeadName(event.target.value);
                  setError('');
                }}
                disabled={saving}
              />
            </div>

            <section className="crm-split-section">
              <div className="crm-split-section-heading">
                <strong>Contacts</strong>
                <span>{contactIds.length} selected</span>
              </div>
              {contacts.length ? (
                <div className="crm-split-options">
                  {contacts.map((contact) => (
                    <label className="crm-split-option" key={contact.id}>
                      <input
                        type="checkbox"
                        checked={contactIds.includes(contact.id)}
                        onChange={() => toggleId(contact.id, setContactIds)}
                        disabled={saving}
                      />
                      <span>
                        <strong>{contact.name || contact.phone || `Contact #${contact.id}`}</strong>
                        <small>
                          {[contact.designation, contact.phone, contact.isPrimary ? 'Primary' : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </small>
                      </span>
                    </label>
                  ))}
                </div>
              ) : <div className="crm-muted">No contacts are linked.</div>}
              <p className="crm-split-help">Calls and WhatsApp messages linked to selected contacts will move with them.</p>
            </section>

            <section className="crm-split-section">
              <div className="crm-split-section-heading">
                <strong>Linked identifiers</strong>
                <span>{externalIdIds.length} selected</span>
              </div>
              {externalIds.length ? (
                <div className="crm-split-options">
                  {externalIds.map((externalId) => (
                    <label className="crm-split-option" key={externalId.id}>
                      <input
                        type="checkbox"
                        checked={externalIdIds.includes(externalId.id)}
                        onChange={() => toggleId(externalId.id, setExternalIdIds)}
                        disabled={saving}
                      />
                      <span>
                        <strong>{EXTERNAL_ID_LABELS[externalId.type] || externalId.type}</strong>
                        <small>{externalIdValue(externalId)}</small>
                      </span>
                    </label>
                  ))}
                </div>
              ) : <div className="crm-muted">No identifiers are linked.</div>}
              <p className="crm-split-help">Orders will follow a moved OMS Account identifier.</p>
            </section>

            {hasSelection && (
              <label className="crm-split-confirmation">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => {
                    setConfirmed(event.target.checked);
                    setError('');
                  }}
                  disabled={saving}
                />
                Move {contactIds.length} contact{contactIds.length === 1 ? '' : 's'} and {externalIdIds.length} identifier{externalIdIds.length === 1 ? '' : 's'} to the new Lead.
              </label>
            )}

            {error && <div className="crm-form-error">{error}</div>}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !hasSelection || !confirmed}>
              {saving ? 'Splitting…' : 'Split Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmSplitLeadDialog;
